import { type UseMutationResult } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'

import { Badge } from 'slab'

import { DrawerProvider, useDrawer } from './Drawer'
import Notes, { type Note } from './Notes'
import { type GrapeAPIErrorType } from '../hooks/GrapeAPIError'
import { type CreateNoteParams } from '../hooks/useCreateNote'

interface NotesStatusComponentProps {
  isLoading: boolean
  notesCount: number
}

const NotesStatusComponent = ({ isLoading, notesCount }: NotesStatusComponentProps) => {
  if (isLoading) {
    return <Loader2 size={16} className='animate-spin text-gray-500' />
  }

  return (
    <div className='flex items-center justify-center text-xs font-medium text-gray-600'>
      <Badge type='info'>{notesCount}</Badge>
    </div>
  )
}

// Component that handles auto-expansion (must be inside DrawerProvider)
const NotesAutoExpander = ({ notes }: { notes?: Note[] }) => {
  const { setIsExpanded } = useDrawer()

  // Expand the drawer if there are 3 or more notes.
  useEffect(() => {
    if (notes && notes.length >= 3) {
      setIsExpanded(true)
    }
  }, [notes, setIsExpanded])

  return null // This component only handles side effects
}

const NotesHeader = () => (
  <h2 className='h2 m-0'>Notes</h2>
)

export interface NotesWithDrawerProps {
  error: GrapeAPIErrorType | null
  isLoading: boolean
  createNoteMutation: UseMutationResult<Note, GrapeAPIErrorType, CreateNoteParams, unknown>
  displayExistingNotes?: boolean
  notes: Note[]
  queryKey: (string | undefined)[]
  isInitialLoadComplete: boolean
  isUnauthorized: boolean
}

const NotesWithDrawer = ({
  error,
  isLoading,
  createNoteMutation,
  displayExistingNotes = true,
  notes,
  queryKey,
  isInitialLoadComplete,
  isUnauthorized,
}: NotesWithDrawerProps) => {
  const notesCount = notes?.length || 0

  // Don't display if initial data load hasn't completed or if the user's
  // not authorized to view the data
  if (!isInitialLoadComplete || isUnauthorized) {
    return null
  }

  return (
    <DrawerProvider
      statusComponent={
        <NotesStatusComponent isLoading={isLoading} notesCount={notesCount} />
      }
      headerComponent={<NotesHeader />}
    >
      {/* Auto-expander inside DrawerProvider so it can access drawer context */}
      <NotesAutoExpander notes={notes} />
      <div className='h-full flex flex-col overflow-hidden' data-drawer='notes'>
        {/* Scrollable content area */}
        <div className='flex-1 overflow-y-auto overflow-x-hidden p-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400'>
          <Notes
            createNoteMutation={createNoteMutation}
            displayExistingNotes={displayExistingNotes}
            error={error}
            isLoading={isLoading}
            notes={notes || []}
            queryKey={queryKey}
          />
        </div>
      </div>
    </DrawerProvider>
  )
}

export default NotesWithDrawer
